import {resolveBackground} from '#core/utils/color-utils.js';
import {isMobile} from '#core/utils/context-utils.js';
import {merge} from '#core/utils/config-utils.js';

import {generateToggleButton} from '#core/application/modules/tiles/button/index.js';
import {generateSkeletonPart} from '#core/application/modules/tiles/skeleton-part/index.js';
import {generateEventsCoupon} from '#core/application/modules/tiles/events-coupon/index.js';
import {generatePriceBoostItem} from '#core/application/modules/tiles/price-boost-item/index.js';
import {generateEventListOffers} from '#core/application/modules/tiles/event-list/index.js';
import {generateEventTimingStatus} from '#core/application/modules/tiles/event-timing-status/index.js';
import {generateBorder} from '#core/application/modules/tiles/border/index.js';
import {CLOSE_BUTTON_TYPES, generateCloseButton} from '#core/application/modules/tiles/close-button/index.js';
import {generateScrollButtonPane} from '#core/application/modules/tiles/scroll-button/index.js';
import {generateToggleButtonRow} from '#core/application/modules/tiles/button/toggle-button/index.js';

export function couponsFactory(PALETTE) {
    // we need to set these styles for some coupons out of coupons generator because by Zeplin some of them should have
    // round corners with their siblings, as example see how TopSports are designed in Zeplin
    const COUPONS_COMMON_STYLES = {
        ...generateBorder(PALETTE.COUPONS_BORDER),
        overflow: 'hidden',
    };
    const SPORT_PAGE_COUPON = {
        eventCoupon: merge(generateEventsCoupon(PALETTE.SPORT_COUPON), {
            initialExpandedGroups: 10,
            container: {
                style: {
                    ...COUPONS_COMMON_STYLES,
                    _mobile_margin: '8px 8px 0', // unable to set padding here because page doesn't have container config
                },
            },
            eventList: {
                header: {
                    _desktop_showTitle: true,
                },
            },
            eventListSkeleton: {
                _desktop_showEventListTitle: true,
            },
        }),
    };
    const SINGLE_LEAGUE_COUPON_EVENT_LIST = PALETTE.SINGLE_LEAGUE_COUPON.EVENTS_COUPON_EVENT_LIST;

    return {
        // only config for NextRaces carousel title here, we need to rethink this configuration
        HomePage: {
            nextRacesController: {
                view: {
                    nextOffTitle: {
                        container: {
                            display: 'flex',
                            height: '40px',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            padding: '0 0 0 8px',
                        },
                        style: {
                            ...PALETTE.COUPONS_TITLE_TEXT,
                        },
                    },
                    raceCardsSkeleton: {
                        title: {
                            container: {
                                display: 'flex',
                                height: '40px',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                padding: '0 0 0 8px',
                            },
                            style: {
                                ...PALETTE.COUPONS_TITLE_TEXT,
                            },
                        },
                    },
                    swiper: {
                        style: {
                            padding: 0,
                        },
                    },
                },
            },
        },
        PrebuiltBetBuilderLeague: {
            container: {
                style: {
                    display: 'block',
                },
            },
        },
        PrebuiltBets: {
            container: {
                style: GENBET_BASED ? {flexDirection: 'column'} : {display: 'none'},
            },
            carouselTitle: {
                style: {
                    ...PALETTE.COUPONS_TITLE_TEXT,
                },
            },
            swiper: {
                style: {
                    scrollPaddingLeft: '8px',
                    scrollPaddingRight: '8px',
                },
            },
        },
        PrebuiltBetBuilderCoupon: {
            container: {
                itemSpacing: 4,
            },
            headerContainer: {
                style: {
                    padding: '0 8px',
                    height: 'initial',
                    minHeight: '47px',
                },
            },
            eventButton: {
                style: {
                    base: {
                        flexShrink: 0,
                        flexGrow: 0,
                    },
                },
            },
            participantsIconInline: {
                participantName: {
                    style: PALETTE.PBB_COUPON_EVENT_NAME_TEXT,
                },
                separator: {
                    style: PALETTE.PBB_COUPON_EVENT_NAME_TEXT,
                },
            },
            eventTimingStatus: generateEventTimingStatus(PALETTE.PBB_COUPON_TIMING_STATUS),
            prebuiltBets: {
                prebuiltBetsSkeleton: {
                    container: {
                        style: {
                            _mobile_padding: '0 8px',
                        },
                    },
                },
            },
        },
        PrebuiltBetBuilderCouponSkeleton: {
            eventContainer: {
                style: {
                    padding: '8px 0',
                    _mobile_padding: 8,
                },
            },
            eventNameLabelSkeleton: generateSkeletonPart(PALETTE.PBB_COUPON_SKELETON),
        },
        PrebuiltBetsView: {
            leaguesFilterSection: {
                navigationMenu: merge(generateToggleButtonRow(PALETTE.PBB_COUPON_SELECTOR), {
                    style: {
                        container: {
                            maxWidth: '100%',
                        },
                    },
                }),
                menuRowSkeleton: {
                    container: {
                        style: {
                            padding: 0,
                            _mobile_padding: '0 8px',
                        },
                    },
                    itemContainer: generateSkeletonPart(PALETTE.PBB_NAVIGATION_MENU_SKELETON),
                },
            },
        },
        TopSports: {
            // TODO: move to feature config
            showMenuSportIcon: true,
            showCouponSportIcon: true,
            style: {
                navigationTitle: {
                    padding: '8px',
                    _mobile_padding: 8,
                    _mobile_textAlign: 'left',
                    ...PALETTE.COUPONS_TITLE_TEXT,
                },
            },
            mainContainer: {
                style: {
                    _mobile_padding: '0 8px',
                },
            },
            couponContainer: {
                style: {
                    ...COUPONS_COMMON_STYLES,
                },
            },
            navigationMenuContainer: {
                style: {
                    ...resolveBackground(PALETTE.TOP_SPORTS_SPORT_SELECTOR_CONTAINER_BG),
                    maxWidth: '100%',
                    borderWidth: '0 0 1px',
                    borderStyle: 'solid',
                    borderColor: PALETTE.TOP_SPORTS_SPORT_SELECTOR_CONTAINER_SEPARATOR_COLOR,
                },
            },
            navigationMenu: generateToggleButtonRow(PALETTE.TOP_SPORTS_SPORT_SELECTOR),
            navigationMenuSkeleton: {
                numberOfItems: 5,
                _mobile_numberOfItems: 4,
                container: {
                    style: {
                        ...resolveBackground(PALETTE.TOP_SPORTS_SPORT_SELECTOR_CONTAINER_BG),
                        boxSizing: 'border-box',
                        width: '100%',
                    },
                },
                iconPlaceholder: generateSkeletonPart(PALETTE.TOP_SPORTS_SKELETON_PART),
                linkPlaceholder: merge(generateSkeletonPart(PALETTE.TOP_SPORTS_SKELETON_PART), {
                    style: {
                        height: '40px',
                        borderRadius: PALETTE.TOP_SPORTS_SPORT_SELECTOR.TOGGLE_BUTTON_BORDER_RADIUS,
                    },
                }),
            },
            eventsCoupon: generateEventsCoupon(PALETTE.TOP_SPORTS_COUPON),
            // top sports skeleton
            skeletonLoader: {
                style: {
                    container: {
                        _mobile_justifyContent: 'flex-start',
                    },
                },
            },
        },
        InPlayContent: {
            container: {
                itemSpacing: 8,
            },
            eventsCouponPerTab: {
                base: merge(generateEventsCoupon(PALETTE.IN_PLAY_COUPON), {
                    container: {
                        style: {
                            ...COUPONS_COMMON_STYLES,
                        },
                    },
                }),
                all: {
                    eventList: {
                        header: {
                            showMarketSelectors: true,
                            _mobile_showMarketSelectors: false,
                        },
                    },
                },
                sport: {
                    eventList: {
                        header: {
                            _mobile_showMarketSelectors: true,
                        },
                    },
                },
            },
            sportTitle: {
                style: {
                    _mobile_textAlign: 'left',
                    ...PALETTE.COUPONS_TITLE_TEXT,
                },
            },
            favoritesContentOnboarding: {
                container: {
                    style: {
                        ...resolveBackground(PALETTE.IN_PLAY_FAVORITES_ONBOARDING_CONTAINER_BG),
                        ...generateBorder(PALETTE.IN_PLAY_FAVORITES_ONBOARDING_CONTAINER_BORDER),
                    },
                },
                header: {
                    style: {
                        ...PALETTE.IN_PLAY_FAVORITES_ONBOARDING_TITLE_TEXT,
                    },
                },
                message: {
                    style: {
                        ...PALETTE.IN_PLAY_FAVORITES_ONBOARDING_MESSAGE_TEXT,
                    },
                },
                closeButton: merge(
                    generateCloseButton(PALETTE.IN_PLAY_FAVORITES_ONBOARDING_HIDE_BUTTON, CLOSE_BUTTON_TYPES.COMMON),
                    {
                        buttonIcon: {
                            style: {
                                width: 8,
                                height: 8,
                                minWidth: 8,
                                minHeight: 8,
                            },
                        },
                    }
                ),
            },
        },
        SingleLeagueCoupon: {
            container: {
                style: {
                    _mobile_padding: 8,
                },
            },
            eventsCoupon: merge(generateEventsCoupon(PALETTE.SINGLE_LEAGUE_COUPON), {
                container: {
                    style: {
                        ...COUPONS_COMMON_STYLES,
                        _mobile_padding: 0,
                    },
                },
                eventList: {
                    header: {
                        container: {
                            style: {
                                borderRadius: '8px 8px 0 0',
                            },
                        },
                    },
                    eventListGroup: {
                        container: {
                            style: {
                                borderRadius: '0 0 8px 8px',
                            },
                        },
                        offersContainer: {
                            style: {
                                padding: 8,
                            },
                        },
                    },
                },
            }),
        },
        // we need this config for EarlyPayoutOffers only to style it inside event list header in SingleLeagueCoupon
        // because they didn't provide us with nested configuration for EPO inside event list header component (Mojito 🤌)
        // remove when MOJ-16895 is delivered and move this config to event list generator
        EarlyPayoutOffers: generateEventListOffers(
            !isMobile()
                ? {
                      EVENT_LIST_GROUP_OFFERS_LABEL_TEXT:
                          SINGLE_LEAGUE_COUPON_EVENT_LIST.EVENT_LIST_HEADER.EVENT_LIST_HEADER_OFFERS_LABEL_TEXT,
                      EVENT_LIST_GROUP_EARLY_PAYOUT_INDICATOR:
                          SINGLE_LEAGUE_COUPON_EVENT_LIST.EVENT_LIST_HEADER.EVENT_LIST_HEADER_EARLY_PAYOUT_INDICATOR,
                  }
                : {
                      EVENT_LIST_GROUP_OFFERS_LABEL_TEXT:
                          SINGLE_LEAGUE_COUPON_EVENT_LIST.EVENT_LIST_MOBILE_HEADER.EVENT_LIST_HEADER_OFFERS_LABEL_TEXT,
                      EVENT_LIST_GROUP_EARLY_PAYOUT_INDICATOR:
                          SINGLE_LEAGUE_COUPON_EVENT_LIST.EVENT_LIST_MOBILE_HEADER
                              .EVENT_LIST_HEADER_EARLY_PAYOUT_INDICATOR,
                  }
        ),
        // Sport Coupon pages
        SportCouponView: SPORT_PAGE_COUPON,
        CmsSportCouponView: SPORT_PAGE_COUPON,
        // Matches
        MatchesView: {
            container: {
                style: {
                    padding: '8px 0 0 0',
                    _mobile_padding: '104px 8px 0',
                },
            },
            contentContainer: {
                style: {
                    ...COUPONS_COMMON_STYLES,
                    ...resolveBackground(PALETTE.MATCHES_VIEW_BG),
                },
            },
            filtersBar: {
                defaultNamingSchemaForDayFilters: GENBET_BASED ? 'EEE d' : 'EEEE',
                buttonScrollPane: generateScrollButtonPane(PALETTE.MATCHES_FILTER_SCROLL_BUTTON),
                filterItem: {
                    button: generateToggleButton(PALETTE.MATCHES_FILTER_BUTTON, true),
                },
                icons: {
                    streaming: {
                        src: 'streaming',
                        style: {
                            fill: PALETTE.MATCHES_FILTER_BUTTON.TOGGLE_BUTTON_ICON_COLOR,
                            width: '14px',
                            height: '14px',
                        },
                    },
                    inplay: {
                        src: 'clock',
                        style: {
                            fill: PALETTE.MATCHES_FILTER_BUTTON.TOGGLE_BUTTON_ICON_COLOR,
                            width: '10px',
                            height: '10px',
                        },
                    },
                },
                selectedIconColor: PALETTE.MATCHES_FILTER_BUTTON.TOGGLE_BUTTON_SELECTED_ICON_COLOR,
                topMenuSkeleton: {
                    buttonSkeleton: generateSkeletonPart({
                        ...PALETTE.MATCHES_FILTER_BUTTON_SKELETON,
                        SKELETON_BORDER_RADIUS: PALETTE.MATCHES_FILTER_BUTTON.TOGGLE_BUTTON_BORDER_RADIUS,
                    }),
                },
            },
        },
        MatchesCoupon: {
            eventsCoupon: {
                ...generateEventsCoupon(PALETTE.MATCHES_COUPON),
                eventListSkeleton: {
                    _desktop_showEventListTitle: false,
                    eventListHeaderContainer: {
                        style: {
                            _desktop_justifyContent: 'end',
                        },
                    },
                },
            },
        },
        LeaguesFilterSection: {
            container: {
                itemSpacing: 8,
                style: {
                    padding: '8px 0 0',
                    _mobile_padding: '90px 0 0 0',
                },
            },
            navigationMenu: {
                style: {
                    container: {
                        padding: 0,
                    },
                },
            },
        },
        // PriceBoost
        PriceBoostsView: {
            container: {
                style: {
                    backgroundColor: 'transparent',
                    margin: '8px 0 0',
                    _mobile_margin: 0,
                },
            },
            leaguesFilterSection: {
                navigationMenu: generateToggleButtonRow(PALETTE.PRICE_BOOST_COUPON_SELECTOR),
                menuRowSkeleton: {
                    container: {
                        style: {
                            padding: 0,
                            _mobile_padding: '0 8px',
                        },
                    },
                    itemContainer: generateSkeletonPart(PALETTE.PRICE_BOOST_NAVIGATION_MENU_SKELETON),
                },
            },
        },
        PriceBoostCoupon: {
            container: {
                style: {
                    padding: 0,
                    _mobile_padding: '0 8px',
                },
                separator: {
                    style: {
                        size: 1,
                        color: PALETTE.PRICE_BOOST_COUPON_EVENT_SEPARATOR_COLOR,
                    },
                },
            },
            sportNameTitle: {
                style: {
                    _mobile_textAlign: 'left',
                    ...PALETTE.COUPONS_TITLE_TEXT,
                },
            },
            eventContainer: {
                style: {
                    ...COUPONS_COMMON_STYLES,
                    ...resolveBackground(PALETTE.PRICE_BOOST_COUPON_EVENTS_BG),
                    padding: 0,
                },
            },
            eventHeaderContainer: {
                style: {
                    padding: '0 8px',
                    ...resolveBackground(PALETTE.PRICE_BOOST_COUPON_EVENTS_HEADER_BG),
                },
            },
            eventTimingStatus: generateEventTimingStatus(PALETTE.PRICE_BOOST_COUPON_TIMING_STATUS),
            eventNameButton: {
                style: {
                    base: {
                        flexShrink: 0,
                        flexGrow: 0,
                    },
                },
            },
            eventNameLabel: {
                container: {
                    padding: '2px 0',
                },
                style: {
                    ...PALETTE.PRICE_BOOST_COUPON_EVENT_NAME_TEXT,
                },
            },
            participantsIconInline: {
                participantName: {
                    style: PALETTE.PRICE_BOOST_COUPON_EVENT_NAME_TEXT,
                },
                separator: {
                    style: PALETTE.PRICE_BOOST_COUPON_EVENT_NAME_TEXT,
                },
            },
            marketContent: {
                priceBoostItem: merge(generatePriceBoostItem(PALETTE.PRICE_BOOST_COUPON_ITEM), {
                    container: {
                        style: {
                            padding: 8,
                        },
                    },
                }),
            },
        },
        PriceBoostMarketContent: {
            container: {
                separator: {
                    style: {
                        size: 1,
                        color: PALETTE.PRICE_BOOST_COUPON_EVENT_SEPARATOR_COLOR,
                    },
                },
            },
        },
        PriceBoostCouponSkeleton: {
            eventsContainer: {
                style: {
                    _mobile_padding: '0 8px',
                },
            },
            container: {
                style: {
                    ...COUPONS_COMMON_STYLES,
                    ...resolveBackground(PALETTE.PRICE_BOOST_COUPON_BG),
                },
            },
            marketGroupContainer: {
                separator: {
                    style: {
                        color: PALETTE.PRICE_BOOST_COUPON_EVENT_SEPARATOR_COLOR,
                    },
                },
            },
            startTime: generateSkeletonPart(PALETTE.PRICE_BOOST_COUPON_SKELETON),
            eventNameLabel: generateSkeletonPart(PALETTE.PRICE_BOOST_COUPON_SKELETON),
            selection: generateSkeletonPart(PALETTE.PRICE_BOOST_COUPON_SKELETON),
            selectionLabel: generateSkeletonPart(PALETTE.PRICE_BOOST_COUPON_SKELETON),
            marketLabel: generateSkeletonPart(PALETTE.PRICE_BOOST_COUPON_SKELETON),
        },
    };
}
